import { useMemo, useRef, useState } from "react";
import InfoTooltip from "../../components/common/InfoTooltip";
import { SALON_SPEAK_TOOLTIPS } from "../../constants/salonSpeakTooltips";
import {
  canRecordAudio,
  canRecognizeSpeech,
  chooseBranchTurn,
  scoreAttempt,
  startVoiceCapture,
} from "./speechUtils";

const buildSummary = (responses = []) => {
  if (!responses.length) {
    return {
      score: 0,
      keywords: [],
      politenessHits: 0,
      responses: 0,
    };
  }
  const totalScore = responses.reduce((sum, item) => sum + (item.score || 0), 0);
  const keywordSet = new Set();
  let politenessHits = 0;
  responses.forEach((item) => {
    (item.keywordsHit || []).forEach((word) => keywordSet.add(word));
    if (item.politenessFound) politenessHits += 1;
  });

  return {
    score: Math.round(totalScore / responses.length),
    keywords: Array.from(keywordSet),
    politenessHits,
    responses: responses.length,
  };
};

export default function SalonSpeakRoleplay({
  scenarios,
  onSaveAttempt,
  salonMode,
  onToggleSalonMode,
}) {
  const [selectedScenarioId, setSelectedScenarioId] = useState(scenarios?.[0]?.id || "");
  const [running, setRunning] = useState(false);
  const [turnIndex, setTurnIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const recorderRef = useRef(null);

  const scenario = useMemo(
    () => (scenarios || []).find((item) => item.id === selectedScenarioId) || null,
    [scenarios, selectedScenarioId]
  );

  const currentTurn = running ? scenario?.turns?.[turnIndex] || null : null;
  const isFinished = running && !currentTurn;

  const resetRun = () => {
    setRunning(false);
    setTurnIndex(0);
    setResponses([]);
    setSaved(false);
    setError("");
  };

  const startRun = () => {
    if (!scenario) return;
    setRunning(true);
    setTurnIndex(0);
    setResponses([]);
    setSaved(false);
    setError("");
  };

  const recordTurn = async () => {
    if (!currentTurn || recording) return;
    setRecording(true);
    setError("");

    const finishTurn = async ({ transcript }) => {
      const score = scoreAttempt({
        transcript: transcript || "",
        importantWords: currentTurn.expectedKeywords || [],
        attemptRecorded: true,
      });
      const entry = {
        turnIndex,
        transcript: transcript || "Transcript unavailable",
        score: transcript ? score.total : Math.max(60, score.total),
        keywordsHit: score.keywordsHit,
        politenessFound: score.politenessFound,
      };

      const next = chooseBranchTurn(currentTurn.next || {}, transcript || "", turnIndex + 1);
      const nextIndex = Number.isFinite(next) ? next : turnIndex + 1;
      setResponses((prev) => [...prev, entry]);
      setTurnIndex(nextIndex >= 99 ? Number.MAX_SAFE_INTEGER : nextIndex);
      setRecording(false);
    };

    try {
      const controller = await startVoiceCapture({
        salonMode,
        onComplete: (result) => finishTurn(result),
        onError: (reason) => setError(reason || "Recording failed"),
      });
      recorderRef.current = controller;
    } catch (err) {
      setRecording(false);
      setError("Recording not available. You can still self-check and continue.");
      if (!canRecognizeSpeech() && !canRecordAudio()) {
        await finishTurn({ transcript: null });
      }
    }
  };

  const stopRecord = () => {
    recorderRef.current?.stop?.();
    recorderRef.current = null;
  };

  const summary = buildSummary(responses);

  const handleSave = async () => {
    if (!scenario || !responses.length || saved) return;
    await onSaveAttempt?.({
      attempt_type: "roleplay",
      content_id: scenario.id,
      transcript: responses.map((item) => item.transcript).join(" | "),
      score: summary.score,
      keywords_hit: summary.keywords,
    });
    setSaved(true);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
        <div className="text-sm font-semibold text-slate-900">Scenario Selector</div>
        <div className="grid gap-2 md:grid-cols-2">
          {(scenarios || []).map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedScenarioId(item.id)}
              className={`rounded-xl border px-3 py-2 text-left ${
                selectedScenarioId === item.id
                  ? "border-rose-300 bg-rose-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="text-sm font-semibold text-slate-900">{item.title}</div>
              <div className="text-xs text-slate-500">{item.description}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <span>Salon mode</span>
            <InfoTooltip text={SALON_SPEAK_TOOLTIPS.roleplay.salonMode} label="Salon mode" />
            <input
              type="checkbox"
              checked={Boolean(salonMode)}
              onChange={(event) => onToggleSalonMode?.(event.target.checked)}
              className="h-4 w-4"
            />
          </label>
          <button
            type="button"
            onClick={running ? resetRun : startRun}
            className="h-10 rounded-full bg-slate-900 px-4 text-sm font-semibold text-white"
          >
            {running ? "Reset" : "Start Roleplay"}
          </button>
        </div>
      </div>

      {running ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
          {!isFinished && currentTurn ? (
            <>
              <div className="rounded-xl border border-slate-200 p-3 space-y-1">
                <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                  Client Prompt
                </div>
                <div className="text-sm text-slate-900">{currentTurn.clientEn}</div>
                <div className="text-xs text-slate-500">{currentTurn.clientVi}</div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                  Record Response
                  <InfoTooltip text={SALON_SPEAK_TOOLTIPS.roleplay.record} label="Record" />
                  <InfoTooltip text={SALON_SPEAK_TOOLTIPS.roleplay.grading} label="Grading" />
                </div>
                <button
                  type="button"
                  onClick={recording ? stopRecord : recordTurn}
                  className={`h-14 min-w-[200px] rounded-full px-6 text-base font-semibold text-white ${
                    recording ? "bg-amber-500" : "bg-rose-500"
                  }`}
                >
                  {recording ? "Stop Recording" : "Record Reply"}
                </button>
                <div className="text-xs text-slate-500">
                  Use polite service language and include expected keywords.
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-sm font-semibold text-emerald-800">Roleplay Summary</div>
                <div className="mt-2 grid gap-2 text-sm text-slate-700 md:grid-cols-3">
                  <div>Average score: {summary.score}/100</div>
                  <div>Keyword hits: {summary.keywords.length}</div>
                  <div>Polite responses: {summary.politenessHits}/{summary.responses}</div>
                </div>
                <div className="mt-2 text-xs text-slate-600">
                  Keywords used: {summary.keywords.join(", ") || "No required keywords detected yet."}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={startRun}
                  className="h-10 rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700"
                >
                  Try Again
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="h-10 rounded-full bg-slate-900 px-4 text-sm font-semibold text-white disabled:opacity-60"
                  disabled={saved}
                >
                  {saved ? "Saved" : "Save to Progress"}
                </button>
              </div>
            </div>
          )}

          {responses.length ? (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">
                Attempts
              </div>
              <div className="mt-2 space-y-2">
                {responses.map((item) => (
                  <div key={`turn-${item.turnIndex}-${item.transcript}`} className="rounded-lg bg-slate-50 px-2 py-2 text-sm">
                    <div className="font-semibold text-slate-700">
                      Turn {item.turnIndex + 1} - Score {item.score}
                    </div>
                    <div className="text-slate-600">{item.transcript}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {error}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
